// Shared live-billing math for a running session.
//
// This walks day boundaries instead of iterating every elapsed minute, so it
// stays cheap even for a session that's been running for hours or days (a
// per-minute loop re-run every second on a tick would otherwise grow
// unbounded and start to cost real time on the main thread). It always
// prefers the owner's CURRENT settings for the happy-hour rate/window over
// whatever was frozen on the session at start time, so a table already
// running when happy hour is turned on (or edited) picks up the change
// immediately — matching what the backend does at stop time.
//
// This is a live on-screen ESTIMATE only; the authoritative amount is always
// computed server-side (minute-by-minute) when the session actually stops.

// getUTCDay() order (0=Sun..6=Sat) mapped to the schedule's day keys.
const JS_DAY_TO_KEY = [
  "sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday",
];

function hhmmToMinutes(hhmm) {
  if (!hhmm || typeof hhmm !== "string") return null;
  const [hStr, mStr] = hhmm.split(":");
  const h = Number(hStr);
  const m = Number(mStr);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

/**
 * Minutes of [rangeStartMs, rangeEndMs) that fall inside happy hour.
 * Prefers the owner's per-day schedule (matching the backend exactly,
 * including per-day enable/disable); falls back to the legacy single daily
 * [hStart, hEnd) window when no schedule is configured. Note: like the
 * legacy window, per-day windows that cross midnight aren't modeled here.
 */
export function happyMinutesInRange(rangeStartMs, rangeEndMs, schedule, hStart, hEnd) {
  const IST_OFFSET_MS = 330 * 60000;
  const DAY_MS = 24 * 60 * 60000;
  const istStart = rangeStartMs + IST_OFFSET_MS;
  const istEnd = rangeEndMs + IST_OFFSET_MS;

  let happyMs = 0;
  let dayStart = Math.floor(istStart / DAY_MS) * DAY_MS;
  while (dayStart < istEnd) {
    let startMin = hStart;
    let endMin = hEnd;
    if (schedule) {
      const dayKey = JS_DAY_TO_KEY[new Date(dayStart).getUTCDay()];
      const dayCfg = schedule[dayKey];
      if (!dayCfg || dayCfg.enabled === false) {
        dayStart += DAY_MS;
        continue;
      }
      startMin = hhmmToMinutes(dayCfg.start);
      endMin = hhmmToMinutes(dayCfg.end);
    } else if (startMin != null) {
      // Legacy fallback fields are whole hours, not HH:MM.
      startMin = startMin * 60;
      endMin = endMin * 60;
    }
    if (startMin != null && endMin != null && startMin < endMin) {
      const windowStart = dayStart + startMin * 60000;
      const windowEnd = dayStart + endMin * 60000;
      const overlapStart = Math.max(istStart, windowStart);
      const overlapEnd = Math.min(istEnd, windowEnd);
      if (overlapEnd > overlapStart) happyMs += overlapEnd - overlapStart;
    }
    dayStart += DAY_MS;
  }
  return happyMs / 60000;
}

/**
 * Full live-billing breakdown for a running session, given the already
 * resolved per-type normal rate (member / cross-club member / non-member,
 * including any per-table override) for this session.
 */
export function computeLiveBreakdown(sess, nowMs, settings, rateNormal) {
  const start = new Date(sess.start_time).getTime();
  const totalMin = Math.max(1, Math.floor((nowMs - start) / 60000));
  const schedule = settings?.happy_hour_schedule ?? sess.happy_hour_schedule;
  const hStart = settings?.happy_hour_start ?? sess.happy_hour_start;
  const hEnd = settings?.happy_hour_end ?? sess.happy_hour_end;
  const rateHappy = settings?.rate_happy_hour ?? sess.rate_happy_hour;

  const happyRaw = happyMinutesInRange(start, start + totalMin * 60000, schedule, hStart, hEnd);
  const happyMinutes = Math.round(happyRaw);
  const normalMinutes = totalMin - happyMinutes;
  const amount = (happyRaw / 60) * rateHappy + (totalMin - happyRaw) / 60 * rateNormal;

  return {
    total_minutes: totalMin,
    happy_minutes: happyMinutes,
    normal_minutes: normalMinutes,
    rate_happy: rateHappy,
    rate_normal: rateNormal,
    amount_happy: (happyRaw / 60) * rateHappy,
    amount_normal: ((totalMin - happyRaw) / 60) * rateNormal,
    amount,
  };
}
